import React from 'react';
import { Box, Container, Typography, Card, CardContent, Button } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { colors } from '../theme';
import RevealOnScroll from './RevealOnScroll';

interface PartnerCard {
  icon: React.ReactNode;
  title: string;
  description: string;
  features: string[];
  ctaText: string;
}

const partnerCards: PartnerCard[] = [
  {
    icon: <StorefrontIcon sx={{ fontSize: 60 }} />,
    title: 'Produtos para Revenda',
    description: 'Produtos profissionais para sua barbearia vender com margem',
    features: [
      'Margem alta para sua barbearia',
      'Produtos de qualidade comprovada',
      'Suporte técnico completo',
      'Material de divulgação incluído',
    ],
    ctaText: 'Quero revender',
  },
];

const PartnersSection: React.FC = () => {
  const handleCTAClick = () => {
    const whatsappNumber = '5511945018356';
    const message = encodeURIComponent(
      'Olá! Vim pelo site da Barba Barão e gostaria de conversar sobre parceria.'
    );
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
  };

  return (
    <Box
      id="parceiros"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: colors.beige,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '300px', md: '500px' },
          height: { xs: '300px', md: '500px' },
          backgroundImage: 'url(/vertical_brand.svg)',
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          opacity: 0.04,
          zIndex: 0,
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <RevealOnScroll>
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
            <Typography
              variant="h2"
              component="h2"
              sx={{
                color: colors.blackMatte,
                mb: 2,
                fontWeight: 700,
              }}
            >
              Quer Revender Barba Barão?
            </Typography>
            <Box
              sx={{
                width: '80px',
                height: '3px',
                backgroundColor: colors.agedGold,
                mx: 'auto',
                mb: 3,
              }}
            />
            <Typography
              variant="h5"
              sx={{
                color: colors.blackMatte,
                maxWidth: '800px',
                mx: 'auto',
                fontWeight: 400,
                fontSize: { xs: '1.2rem', md: '1.5rem' },
              }}
            >
              Parceria real. Se o cliente da sua barbearia gosta, nós vencemos. Simples!
            </Typography>
          </Box>
        </RevealOnScroll>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 4, md: 6 },
          }}
        >
          {partnerCards.map((card, index) => (
            <Box key={index} sx={{ flex: 1 }}>
              <RevealOnScroll delay={index * 150}>
                <Card
                  sx={{
                    height: '100%',
                    backgroundColor: colors.blackMatte,
                    border: `3px solid ${colors.agedGold}`,
                    borderRadius: '8px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 12px 24px ${colors.darkWine}40`,
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      p: { xs: 3, md: 4 },
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mb: 3,
                        color: colors.agedGold,
                      }}
                    >
                      {card.icon}
                    </Box>

                    <Typography
                      variant="h4"
                      component="h3"
                      sx={{
                        color: colors.beige,
                        mb: 2,
                        textAlign: 'center',
                        fontWeight: 700,
                      }}
                    >
                      {card.title}
                    </Typography>

                    <Typography
                      variant="h6"
                      sx={{
                        color: colors.agedGold,
                        mb: 3,
                        textAlign: 'center',
                        fontWeight: 500,
                        fontSize: { xs: '1rem', md: '1.1rem' },
                      }}
                    >
                      {card.description}
                    </Typography>

                    <Box sx={{ mb: 4, flex: 1 }}>
                      {card.features.map((feature, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            mb: 2,
                          }}
                        >
                          <Box
                            sx={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: colors.agedGold,
                              mt: '8px',
                              mr: 2,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.beige,
                              lineHeight: 1.7,
                              fontSize: { xs: '0.95rem', md: '1rem' },
                            }}
                          >
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      onClick={handleCTAClick}
                      aria-label={card.ctaText}
                      sx={{
                        py: 2,
                        fontSize: '1.1rem',
                        backgroundColor: colors.darkWine,
                        color: colors.beige,
                        border: `2px solid ${colors.agedGold}`,
                        fontWeight: 700,
                        '&:hover': {
                          backgroundColor: colors.agedGold,
                          color: colors.blackMatte,
                        },
                      }}
                    >
                      {card.ctaText}
                    </Button>
                  </CardContent>
                </Card>
              </RevealOnScroll>
            </Box>
          ))}
        </Box>

        <RevealOnScroll delay={300}>
          <Box
            sx={{
              mt: { xs: 6, md: 10 },
              p: { xs: 3, md: 5 },
              backgroundColor: colors.darkWine,
              border: `2px solid ${colors.agedGold}`,
              borderRadius: '8px',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: colors.beige,
                mb: 2,
                fontWeight: 700,
              }}
            >
              Pronto para Falar com um Humano?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: colors.beige,
                mb: 3,
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.7,
              }}
            >
              Aqui é no papo reto. Sem robô, sem enrolação. Conversamos direto, respondemos rápido e
              tratamos cada parceiro com respeito.
            </Typography>
            <Button
              variant="outlined"
              size="large"
              onClick={handleCTAClick}
              aria-label="Quero falar com um humano"
              sx={{
                px: 5,
                py: 2,
                fontSize: '1.1rem',
                borderColor: colors.agedGold,
                color: colors.beige,
                borderWidth: 2,
                fontWeight: 700,
                '&:hover': {
                  borderWidth: 2,
                  backgroundColor: colors.beige,
                  color: colors.blackMatte,
                  borderColor: colors.beige,
                },
              }}
            >
              Quero falar com um humano
            </Button>
          </Box>
        </RevealOnScroll>
      </Container>
    </Box>
  );
};

export default PartnersSection;
